import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react';
import {
  Box, CircularProgress, IconButton, List, ListItemButton, Paper, Popper, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddPhotoAlternate as PhotoIcon,
  Close as CloseIcon,
  GifBox as GifIcon,
  InsertEmoticon as EmojiIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import EmojiPickerPopover from './EmojiPickerPopover';
import GifPickerDialog from './GifPickerDialog';
import GiphyAttribution from './GiphyAttribution';
import { convertAllEmoticons, convertTrailingEmoticon } from '../../utils/emoticons';
import { getShortcodeQuery, loadEmojiIndex, searchEmojiIndex } from '../../utils/emojiSearch';
import {
  getClipboardFiles, getPhotoFileError, pickPhotoFile, renamePastedImage,
} from './chatPhotoFiles';

export { MAX_PHOTO_BYTES } from './chatPhotoFiles';
export const MAX_MESSAGE_LENGTH = 2000;
const COUNTER_THRESHOLD = 1800;
// On phones the attach buttons are packed tightly (32px wide, still 44px tall) so the message box gets the room.
// `!important` beats the app-wide 44px minimum touch width for icon buttons in App.css.
const toolButtonSx = (theme) => ({
  mb: 0.25,
  [theme.breakpoints.down('sm')]: {
    minWidth: '32px !important',
    width: 32,
    px: 0,
    '& .MuiSvgIcon-root': { fontSize: 22 },
  },
});

/**
 * Message input with an emoji picker, emoticon auto-conversion (":)" → 🙂 after a space),
 * ":shortcode" suggestions, and a single photo or GIPHY GIF attachment with optional caption. Photos can be
 * chosen with the photo button or pasted; the ref exposes `attachFile(file)` for drag-and-drop.
 *
 * `onSendText(content)` / `onSendPhoto(file, caption)` / `onSendGif(gif, caption)` resolve true when sent,
 * which clears the input. The GIF button is shown only when `onSendGif` is provided.
 */
const ChatComposer = forwardRef(({
  channelName, isDesktop, disabled, onSendText, onSendPhoto, onSendGif, onError, autoFocus = false,
}, ref) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [gif, setGif] = useState(null);
  const [gifPickerOpen, setGifPickerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(0);

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const anchorRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const pendingCaretRef = useRef(null);
  const shortcodeRef = useRef(null);

  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret != null && inputRef.current) {
      inputRef.current.setSelectionRange(caret, caret);
      pendingCaretRef.current = null;
    }
  }, [draft]);

  useEffect(() => () => {
    if (photo?.previewUrl) URL.revokeObjectURL?.(photo.previewUrl);
  }, [photo]);

  // Opened from a notification's "Reply" action.
  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const closeSuggestions = () => {
    shortcodeRef.current = null;
    setSuggestions([]);
    setActiveSuggestion(0);
  };

  const updateSuggestions = useCallback((value, caret) => {
    const shortcode = getShortcodeQuery(value, caret);
    shortcodeRef.current = shortcode;
    if (!shortcode) {
      setSuggestions([]);
      return;
    }
    loadEmojiIndex()
      .then((index) => {
        // Ignore results for a query the user has already typed past.
        if (shortcodeRef.current !== shortcode) return;
        setSuggestions(searchEmojiIndex(index, shortcode.query));
        setActiveSuggestion(0);
      })
      .catch(() => setSuggestions([]));
  }, []);

  const handleChange = (event) => {
    let { value } = event.target;
    let caret = event.target.selectionStart ?? value.length;
    const converted = convertTrailingEmoticon(value, caret);
    if (converted) {
      ({ value, caret } = converted);
      pendingCaretRef.current = caret;
    }
    setDraft(value);
    updateSuggestions(value, caret);
  };

  const insertText = (text, replaceFrom = null) => {
    const input = inputRef.current;
    const start = replaceFrom ?? input?.selectionStart ?? draft.length;
    const end = input?.selectionEnd ?? draft.length;
    const next = (draft.slice(0, start) + text + draft.slice(Math.max(end, start))).slice(0, MAX_MESSAGE_LENGTH);
    pendingCaretRef.current = Math.min(start + text.length, next.length);
    setDraft(next);
  };

  const chooseSuggestion = (item) => {
    const shortcode = shortcodeRef.current;
    if (!shortcode) return;
    insertText(`${item.emoji} `, shortcode.start);
    closeSuggestions();
    inputRef.current?.focus();
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /** Validates and attaches a photo (photo button, paste, or drop). Returns true when attached. */
  const attachFile = (file, { pasted = false } = {}) => {
    if (!file || disabled || sending) return false;
    const fileError = getPhotoFileError(file);
    if (fileError) {
      onError(fileError);
      return false;
    }
    const attached = pasted ? renamePastedImage(file) : file;
    setGif(null);
    setPhoto({ file: attached, previewUrl: URL.createObjectURL ? URL.createObjectURL(attached) : null });
    return true;
  };

  // One attachment per message: choosing a GIF replaces any photo.
  const attachGif = (selected) => {
    clearPhoto();
    setGif(selected);
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    attachFile: (file) => {
      const attached = attachFile(file);
      if (attached) inputRef.current?.focus();
      return attached;
    },
  }));

  const handlePhotoSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!attachFile(file)) clearPhoto();
  };

  const handlePaste = (event) => {
    const { clipboardData } = event;
    const file = pickPhotoFile(getClipboardFiles(clipboardData));
    // Plain-text pastes are left to the browser.
    if (!file) return;
    event.preventDefault();
    attachFile(file, { pasted: true });
    const text = clipboardData.getData?.('text/plain') || '';
    // Copying a file in a file manager also puts its name on the clipboard; don't use that as a caption.
    if (text && text.trim() !== file.name) insertText(text);
  };

  const submit = async () => {
    const submittedDraft = draft;
    const submittedPhoto = photo;
    const submittedGif = gif;
    const content = convertAllEmoticons(submittedDraft.trim());
    if (sending || disabled || (!content && !submittedPhoto && !submittedGif)) return;
    setSending(true);
    closeSuggestions();
    try {
      let sent;
      if (submittedGif) sent = await onSendGif(submittedGif, content);
      else if (submittedPhoto) sent = await onSendPhoto(submittedPhoto.file, content);
      else sent = await onSendText(content);
      if (sent) {
        // The input stays editable while sending; keep anything typed after submitting.
        setDraft((current) => (current === submittedDraft ? '' : current));
        if (submittedPhoto) {
          setPhoto((current) => (current === submittedPhoto ? null : current));
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
        if (submittedGif) setGif((current) => (current === submittedGif ? null : current));
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveSuggestion((i) => (i + 1) % suggestions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveSuggestion((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        chooseSuggestion(suggestions[activeSuggestion]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSuggestions();
        return;
      }
    }
    // Enter sends on desktop; on phones Enter inserts a newline and the send button is used.
    if (isDesktop && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const canSend = !sending && !disabled && (draft.trim().length > 0 || Boolean(photo) || Boolean(gif));

  return (
    <Box
      component="form"
      onSubmit={(event) => { event.preventDefault(); submit(); }}
      sx={{
        px: { xs: 1, sm: 2 },
        py: 1,
        pb: { xs: 'calc(8px + env(safe-area-inset-bottom))', sm: 1 },
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      {photo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }} data-testid="chat-photo-preview">
          {photo.previewUrl ? (
            <Box
              component="img"
              src={photo.previewUrl}
              alt="Selected photo"
              sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
            />
          ) : (
            <PhotoIcon color="action" />
          )}
          <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {photo.file.name || 'Photo'} — add a caption (optional)
          </Typography>
          <IconButton size="small" onClick={clearPhoto} aria-label="Remove photo" disabled={sending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {gif && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }} data-testid="chat-gif-preview">
          <Box
            component="img"
            src={gif.previewUrl}
            alt={gif.title || 'Selected GIF'}
            sx={{
              width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {gif.title || 'GIF'} — add a caption (optional)
            </Typography>
            <GiphyAttribution sx={{ color: 'text.secondary' }} />
          </Box>
          <IconButton size="small" onClick={() => setGif(null)} aria-label="Remove GIF" disabled={sending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
        <Box
          role="group"
          aria-label="Attach"
          data-testid="chat-composer-tools"
          sx={{ display: 'flex', flexShrink: 0, gap: { xs: 0, sm: 0.5 }, ml: { xs: -0.5, sm: 0 } }}
        >
          <Tooltip title="Emoji">
            <span>
              <IconButton
                ref={emojiButtonRef}
                onClick={() => setPickerOpen(true)}
                aria-label="Add emoji"
                disabled={disabled}
                sx={toolButtonSx}
              >
                <EmojiIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Photo">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                aria-label="Add photo"
                disabled={disabled || sending}
                sx={toolButtonSx}
              >
                <PhotoIcon />
              </IconButton>
            </span>
          </Tooltip>
          {onSendGif && (
            <Tooltip title="GIF">
              <span>
                <IconButton
                  onClick={() => setGifPickerOpen(true)}
                  aria-label="Add GIF"
                  disabled={disabled || sending}
                  sx={toolButtonSx}
                >
                  <GifIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handlePhotoSelected}
          data-testid="chat-photo-input"
        />
        <TextField
          ref={anchorRef}
          inputRef={inputRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => setTimeout(closeSuggestions, 150)}
          placeholder={photo || gif ? 'Add a caption…' : `Message ${channelName}`}
          multiline
          maxRows={4}
          fullWidth
          size="small"
          disabled={disabled}
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'aria-label': 'Message' }}
          helperText={draft.length >= COUNTER_THRESHOLD ? `${draft.length}/${MAX_MESSAGE_LENGTH}` : undefined}
        />
        <IconButton
          type="submit"
          color="primary"
          aria-label={(gif && 'Send GIF') || (photo && 'Send photo') || 'Send message'}
          disabled={!canSend}
          sx={{ mb: 0.25 }}
        >
          {sending ? <CircularProgress size={22} /> : <SendIcon />}
        </IconButton>
      </Box>

      <Popper
        open={suggestions.length > 0}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper elevation={4} sx={{ mb: 1, minWidth: 240, maxWidth: '90vw' }}>
          <List dense aria-label="Emoji suggestions" role="listbox">
            {suggestions.map((item, index) => (
              <ListItemButton
                key={item.emoji}
                role="option"
                selected={index === activeSuggestion}
                aria-selected={index === activeSuggestion}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseSuggestion(item)}
              >
                <Box component="span" sx={{ fontSize: '1.4rem', mr: 1.5 }}>{item.emoji}</Box>
                <Typography variant="body2" noWrap>:{item.name.replace(/\s+/g, '_')}:</Typography>
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>

      <EmojiPickerPopover
        open={pickerOpen}
        anchorEl={emojiButtonRef.current}
        onClose={() => setPickerOpen(false)}
        onSelect={(emoji) => insertText(emoji)}
        isMobile={!isDesktop}
      />
      {gifPickerOpen && (
        <GifPickerDialog open onClose={() => setGifPickerOpen(false)} onSelect={attachGif} />
      )}
    </Box>
  );
});

ChatComposer.displayName = 'ChatComposer';

export default ChatComposer;
